import type { PostHog } from "@posthog/react";
import { usePostHog } from "@posthog/react";
import { parseError } from "evlog";
import { RotateCcw, Settings } from "lucide-react";
import { useState } from "react";

import { RestartConfirmDialog } from "#/components/game/RestartConfirmDialog";
import { Button } from "#/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "#/components/ui/dropdown-menu";
import { Switch } from "#/components/ui/switch";
import { useGameSelector } from "#/game/GameStoreProvider";
import type { GuestGameplaySettingKey } from "#/lib/guestGameplaySettings";
import { persistGameplaySetting } from "#/lib/persistGameplaySetting";
import { useRequestGameContext } from "#/lib/useRequestGameContext";

function runToggle({
  apply,
  setting,
  next,
  isAnonymous,
  posthog,
}: {
  apply: (value: boolean) => void;
  setting: GuestGameplaySettingKey;
  next: boolean;
  isAnonymous: boolean;
  posthog: PostHog;
}) {
  apply(next);
  void persistGameplaySetting({ setting, enabled: next, isAnonymous }).catch((err: unknown) => {
    apply(!next);
    const parsed = parseError(err);
    if (parsed.status >= 500) {
      posthog.captureException(err);
    }
  });
}

function SettingCheckboxItem({
  label,
  checked,
  apply,
  setting,
}: {
  label: string;
  checked: boolean;
  apply: (value: boolean) => void;
  setting: GuestGameplaySettingKey;
}) {
  const { isAnonymous } = useRequestGameContext();
  const posthog = usePostHog();
  return (
    <DropdownMenuCheckboxItem
      checked={checked}
      onCheckedChange={(next) => runToggle({ apply, setting, next, isAnonymous, posthog })}
      className="justify-between pr-1.5 [&_[data-slot=dropdown-menu-checkbox-item-indicator]]:hidden"
    >
      {label}
      <Switch
        size="sm"
        checked={checked}
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none"
      />
    </DropdownMenuCheckboxItem>
  );
}

function SettingsToggleGroup() {
  const fastPlayback = useGameSelector((s) => s.fastPlayback);
  const endTurnConfirmation = useGameSelector((s) => s.endTurnConfirmation);
  const disableAiPlayback = useGameSelector((s) => s.disableAiPlayback);
  const setFastPlayback = useGameSelector((s) => s.setFastPlayback);
  const setEndTurnConfirmation = useGameSelector((s) => s.setEndTurnConfirmation);
  const setDisableAiPlayback = useGameSelector((s) => s.setDisableAiPlayback);

  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>Game Settings</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <SettingCheckboxItem
        label="End Turn Confirmation"
        checked={endTurnConfirmation}
        apply={setEndTurnConfirmation}
        setting="endTurnConfirmation"
      />
      <SettingCheckboxItem
        label="Fast Animations"
        checked={fastPlayback}
        apply={setFastPlayback}
        setting="fastAnimations"
      />
      <SettingCheckboxItem
        label="Disable AI Playback"
        checked={disableAiPlayback}
        apply={setDisableAiPlayback}
        setting="disableAiPlayback"
      />
    </DropdownMenuGroup>
  );
}

export function GameSettingsMenu({
  onRestart,
  isRestarting,
  allowRestart = true,
}: {
  onRestart: () => void | Promise<void>;
  isRestarting: boolean;
  allowRestart?: boolean;
}) {
  const [restartOpen, setRestartOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button variant="outline" size="icon-lg" aria-label="Game settings">
              <Settings />
            </Button>
          }
        />
        <DropdownMenuContent side="top" align="end" sideOffset={8} className="w-56 min-w-56">
          <SettingsToggleGroup />
          {allowRestart ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                disabled={isRestarting}
                onClick={() => setRestartOpen(true)}
              >
                <RotateCcw />
                Restart
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {allowRestart ? (
        <RestartConfirmDialog
          open={restartOpen}
          onOpenChange={setRestartOpen}
          onConfirm={onRestart}
          pending={isRestarting}
        />
      ) : null}
    </>
  );
}
